/*
 * Copyright (c) 2024 Pegasystems Inc.
 * All rights reserved.
 */
import type { NodeProps } from '../../../contextree/types';
import { C11nEnv } from '../../../interpreter/c11n-env';

import Expression from './expression';
import type { ExpressionHandlers } from './types';

/**
 * [createExpression]
 * Description : Returns an instance of the Expression for the flat string.
 * @param expression       Flat expression string.
 * @returns Expression instance
 * @param localData data
 */
const createExpression = (expression: string, localData?: object): Expression => {
  const singleEquals = /[a-zA-Z0-9_)"'\s]=[a-zA-Z0-9_("'\s]/;
  // Replace = with ==
  const updatedExpression = expression.replace(singleEquals, (subStr: string) => subStr.replace('=', '=='));
  // Return the expression instance.
  return new Expression(updatedExpression, localData);
};

/**
 * [buildAst]
 * Description : Returns an AST for the expression string.
 * @param expression Instance of the Expression created for the flat expression string.
 * @returns AST
 */
const buildAST = (expression: Expression): Expression | boolean => {
  if (!expression) {
    return true;
  }
  // Build the expression.It will generate the AST
  return expression.buildAST();
};

/**
 * [evaluateExpression]
 * Description : Evaluate the AST generated for the expression and returns the evaluation result
 * @param expression      Instance of the Expression created for the flat expression string.
 * @param store           Redux store
 * @returns Evaluation Result
 */
const evaluateExpression = (
  expression: Expression | boolean,
  store?: C11nEnv,
  contextTreeNodeProps?: NodeProps
): boolean => {
  if (!expression) {
    return true;
  }
  // Evaluate the AST.
  return (expression as Expression).evaluate(store, contextTreeNodeProps);
};

/**
 * This API evaluates the expression either on the given localData or data in the store
 * Note: The localeData will always take the priority over the data in the store
 * @param filterExpression expression that has to be evaluated on the data
 * @param data localData
 * @param c11nEnv c11nEnv object of the current component
 * @returns result of the evaluated expression
 */
const evaluateExpressionOnLocalData = (filterExpression: string, data: object, c11nEnv?: C11nEnv): boolean => {
  const expression = createExpression(filterExpression, data);
  const exprAst = buildAST(expression);
  return evaluateExpression(exprAst, c11nEnv);
};

const isOperationTypeSupported = (
  operationType: string,
  handlers: ExpressionHandlers
): operationType is keyof ExpressionHandlers => {
  return operationType in handlers;
};

export { createExpression, buildAST, evaluateExpression, evaluateExpressionOnLocalData, isOperationTypeSupported };
